import {
  type JobSearchScope,
  type SearchJobsQuery,
} from '@/features/jobs/list/SearchableJobStore'
import { isAbortError } from '@/utils/abort-util'
import { FeatureToggles } from '@/features/feature-toggles'
import type { AlertRetryWrapperFactory } from '@/features/shell'
import { type JobStatus } from '@/features/jobs/job-status'
import { type SearchableJobSortField } from '@/features/jobs/list/job-sort-fields'
import { type JobStatusFilterOption } from '@/features/jobs/list/job-status-filter-option'
import { isEmpty, omit, orderBy, uniq } from 'lodash-es'
import { action, computed, observable, runInAction } from 'mobx'
import {
  combineLatest,
  EMPTY,
  merge,
  type Observable,
  of,
  firstValueFrom,
} from 'rxjs'
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  startWith,
  switchMap,
  takeWhile,
} from 'rxjs/operators'
import {
  type FilterItem,
  FilterItemSource,
  type FilterStorage,
  type FiltersResult,
  FiltersViewModel,
} from '@/infra/FiltersViewModel'
import { CursorPaged, type CursorPageQuery } from '@/infra/paging'
import { Id } from '@/infra/id'
import { InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import { fromStream, toStream, tapAction } from '@/utils/mobx-util'
import { deepEqual } from '@/utils/object-util'
import { shareLatest, whileActive, withAbortSignal } from '@/utils/rx-util'
import { type DateRange, DateRangeFilter } from '@/infra/DateRangeFilter'
import {
  JobPeriodOptions,
  JobScopeUpdatesOptions,
  ProgressionStepOptions,
  StatusOptions,
  WaitingOnOptions,
} from './jobs-table-filter-values'
import { type SearchableJob } from '@/features/jobs/list/SearchableJob'
import { type SearchableJobStore } from '@/features/jobs/list/SearchableJobStore'
import { JobSearchQueryStringBuilder } from './JobSearchQueryStringBuilder'
import { SelectFilter } from '@/infra/SelectFilter'
import { type TableSortOrder } from '@/components/Table'
import { type NumberRange, NumberRangeFilter } from '@/infra/NumberRangeFilter'
import type {
  FilterTeamMembersFn,
  ListUserTeamsFn,
  GetCurrentMemberFn,
  GetCurrentUserWorkspaceMembershipsFn,
  GetUserDataFn,
} from '@/features/iam'
import type { FetchDocumentTypesFn } from '@/features/documents'
import type { ListInventoryItemsFn } from '@/features/inventory'
import { type ListLegendsFn } from '@/features/jobs/legends/PublishedLegendStore'
import { type PublishedLegend } from '@/features/jobs/legends/PublishedLegend'
import { type JobTicketStore } from '@/features/jobs/tickets/JobTicketStore'
import { SearchTextViewModel } from '@/infra/SearchTextViewModel'
import { type JobProgressionStepStatus } from '@/features/jobs/taxes/progression/JobProgression'
import { type JobProgressionStepFilterOption } from '@/features/jobs/list/job-progression-filter-option'
import { InfiniteSelectFilter } from '@/infra/InfiniteSelectFilter'
import { type JobScopeChangeStatusFilterOption } from '@/features/jobs/list/job-scope-change-status-filter-option'
import { type JobScopeChangeStatus } from '@/features/jobs/job-scope-changes/JobScopeChange'
import { SingleSelectFilter } from '@/infra/SingleSelectFilter'
import { type JobPeriodFilterOption } from '@/features/jobs/list/job-period-filter-option'
import { FlagFilter } from '@/infra/FlagFilter'
import { task } from 'mobx-task'
import { type JobListPerspective } from '@/features/jobs/list/job-list-view'
import { type WaitingOn } from '@/features/jobs/Job'
import {
  parseStoredJobPeriodFilterOption,
  parseStoredJobProgressionStepFilterOption,
  parseStoredJobScopeChangeStatusFilterOption,
  parseStoredJobStatusFilterOption,
  parseStoredWaitingOn,
} from './stored-job-list-values'
import {
  SavedJobFilters,
  type SavedJobFilter,
  sharedSavedFilterId,
} from '@/features/jobs/list/saved-job-filters'
import { type SharedJobFilter } from '@/features/jobs/list/shared-job-filter'
import { SavedJobFilterStore } from '@/features/jobs/list/SavedJobFilterStore'
import { isRefreshDesign } from '@/infra/design'
import { type ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'

export interface JobFilter {
  workspaces: Id[]
  jobType: Id[]
  jobStatus: JobStatusFilterOption[]
  progressionStep: JobProgressionStepFilterOption[]
  jobScopeUpdates: JobScopeChangeStatusFilterOption[]
  createdRange: DateRange
  submittedRange: DateRange
  claimedRange: DateRange
  closedRange: DateRange
  providerCutAmountRange?: NumberRange
  team: Id[]
  jobPeriod: JobPeriodFilterOption
  archived: boolean
  readyToAdvance: boolean
  members: Id[]
  serviceItems: Id[]
  documentTypes: Id[]
  waitingOn: WaitingOn[]
  unread: boolean
}

/**
 * Options for {@link JobSearchViewModel}.
 */
export interface JobSearchViewModelOptions {
  /**
   * If specified, pre-processes the list of jobs, such as filtering and
   * sorting.
   */
  models?: (jobs: readonly SearchableJob[]) => readonly SearchableJob[]

  /**
   * Where the filters persist their values.
   */
  filterStorage?: FilterStorage

  /**
   * The viewer's tabs. Without it they get the built-in tabs, kept only
   * until the page reloads.
   */
  savedFilterStore?: SavedJobFilterStore
}

/**
 * Implements job searching which is shared
 * between Job Management, Opportunities, and the Job List in Job Details.
 */
export class JobSearchViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Jobs with unread messages for the current user, which backs the Unread
   * filter.
   */
  @observable.ref private accessor _unreadJobIds: Id[] = []

  /**
   * Backing field for the last search.
   */
  @observable.ref private accessor _lastSearch: JobSearchResult = {
    jobs: [],
    searchFilter: JobSearchFilter.none,
    total: 0,
    filtersApplied: false,
  }

  /**
   * Total jobs for most recent search, plus cumulative job created events.
   */
  @observable accessor totalJobs = 0

  /**
   * Sorting.
   */
  @observable accessor sorting: JobSort = { type: 'Default' }

  /**
   * Applies a date range to the date created filter.
   */
  @observable accessor forcedCreatedDateRange: DateRange | null = null

  /**
   * Search text.
   */
  readonly searchText = new SearchTextViewModel()

  /**
   * Filter by scope.
   */
  @observable accessor scope: JobSearchScope = 'Mine'

  /**
   * The saved filter last picked from the tabs. Consumers without tabs leave
   * this null.
   */
  @observable accessor savedFilterId: string | null = null

  /**
   * A tab opened from a shared link, shown until it's added or dismissed.
   */
  @observable.ref accessor sharedFilter: SharedJobFilter | null = null

  /**
   * Filters to search with instead of the selected tab's, while they're
   * being edited.
   */
  @observable.ref private accessor _savedFilterPreview:
    (() => FiltersResult<JobFilter>) | null = null

  readonly filters

  readonly savedFilterList: SavedJobFilters

  /**
   * Jobs scroller.
   */
  readonly scroller = new InfiniteScrollProvider<
    SearchableJob,
    JobSearchFilter
  >({
    limit: 20,
    fetchItems: ({ paging, filter, signal }) => {
      if (matchesNothing(filter)) {
        return Promise.resolve(CursorPaged.empty<SearchableJob>())
      }

      const withRetry = this._createAlertRetryWrapper(
        'Loading jobs failed',
        signal,
      )
      return withRetry(() =>
        this._fetchJobs(
          toSearchJobsQuery(paging, filter, this.forcedCreatedDateRange),
          signal,
        ),
      )
    },
  })

  /**
   * Subscribes to the pending state of the fetch task.
   * Once it flips to `false`, stop.
   */
  private readonly _getIsFirstFetchRunning = fromStream(
    true,
    toStream(() => this.scroller.fetch.pending, true).pipe(
      takeWhile(Boolean, true),
    ),
  )

  /**
   * Search filter changes.
   */
  private readonly _searchFilter$

  /**
   * Searches observable. Each emitted value is the search result of
   * a completed query.
   */
  readonly searches$

  constructor(
    private readonly _searchableJobStore: SearchableJobStore,
    private readonly _jobTicketStore: JobTicketStore,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _getUserData: GetUserDataFn,
    private readonly _getCurrentUserMemberships: GetCurrentUserWorkspaceMembershipsFn,
    private readonly _filterTeamMembers: FilterTeamMembersFn,
    private readonly _listLegends: ListLegendsFn,
    private readonly _listUserTeams: ListUserTeamsFn,
    private readonly _searchInventoryItems: ListInventoryItemsFn,
    private readonly _fetchDocumentTypes: FetchDocumentTypesFn,
    unreadJobIds$: Observable<Id[]>,
    private readonly _options: JobSearchViewModelOptions = {},
  ) {
    this.savedFilterList = new SavedJobFilters(
      this._options.savedFilterStore ?? new SavedJobFilterStore(null),
      () => this.listPerspective,
    )
    this.filters = new FiltersViewModel<JobFilter>({
      filterStorage: this._options.filterStorage,
      definitions: {
        workspaces: {
          title: 'Workspaces',
          implicit: true,
          factory: (key, title) =>
            new SelectFilter(
              key,
              title,
              // Use dynamic source to ensure workspaces are always fresh
              // (static source captures at creation time which may be too early)
              // eslint-disable-next-line @typescript-eslint/require-await -- SelectFilter expects a Promise-returning source; async also turns a sync throw into a rejection.
              async () => this._getWorkspaceItems(),
              true,
            ),
        },
        jobType: {
          icon: 'hashtag',
          title: 'Job type',
          persist: true,
          factory: (key, title) =>
            new SelectFilter(
              key,
              title,
              async () => this._fetchLegends(),
              true,
            ),
        },
        jobStatus: {
          icon: 'invoices',
          title: 'Status',
          persist: true,
          factory: (key, title) =>
            new SelectFilter(
              key,
              title,
              FilterItemSource.static(StatusOptions),
              true,
              undefined,
              parseStoredJobStatusFilterOption,
            ),
        },
        progressionStep: {
          icon: 'progress',
          title: 'Progress',
          persist: true,
          factory: (key, title) =>
            new SelectFilter(
              key,
              title,
              FilterItemSource.static(ProgressionStepOptions),
              true,
              undefined,
              parseStoredJobProgressionStepFilterOption,
            ),
        },
        jobScopeUpdates: {
          icon: 'pending-approval',
          title: 'Job scope updates',
          persist: true,
          factory: (key, title) =>
            new SelectFilter(
              key,
              title,
              FilterItemSource.static(JobScopeUpdatesOptions),
              true,
              undefined,
              parseStoredJobScopeChangeStatusFilterOption,
            ),
        },
        team: {
          icon: 'teams',
          title: 'Team',
          persist: true,
          factory: (key, title) =>
            new InfiniteSelectFilter(
              key,
              title,
              ({ paging, filter }) => this._fetchUserTeams(paging, filter),
              true,
            ),
        },
        jobPeriod: {
          title: 'Filter by period',
          implicit: true,
          persist: true,
          factory: (key, title) =>
            new SingleSelectFilter(
              title,
              key,
              'All',
              FilterItemSource.static(JobPeriodOptions),
              true,
              parseStoredJobPeriodFilterOption,
            ),
        },
        createdRange: {
          icon: 'date-start',
          title: 'Date created',
          factory: (key, title) => new DateRangeFilter(key, title),
        },
        submittedRange: {
          icon: 'date-submitted',
          title: 'Date submitted',
          factory: (key, title) => new DateRangeFilter(key, title),
        },
        claimedRange: {
          icon: 'date-claimed',
          title: 'Date claimed',
          factory: (key, title) => new DateRangeFilter(key, title),
        },
        closedRange: {
          icon: 'date-completed',
          title: 'Date completed',
          factory: (key, title) => new DateRangeFilter(key, title),
        },
        providerCutAmountRange: {
          icon: 'provider-cut',
          title: 'Payout',
          factory: (key, title) => new NumberRangeFilter(key, title, 'USD'),
        },
        archived: {
          icon: 'delete',
          title: 'Archived',
          implicit: true,
          factory: (key, title) => new FlagFilter(key, title),
        },
        readyToAdvance: {
          icon: 'date-start',
          title: 'Ready to advance',
          isVisible: () =>
            FeatureToggles.pendingJobs() && this.useSearchJobsForAdmin,
          conflictsWith: ['jobStatus'],
          factory: (key, title) => new FlagFilter(key, title, true),
        },
        members: {
          icon: 'add-people',
          title: 'Members',
          implicit: true,
          factory: (key, title) =>
            new SelectFilter(key, title, FilterItemSource.static([]), true),
        },
        serviceItems: {
          icon: 'ranking-money',
          title: 'Service Item',
          persist: true,
          factory: (key, title) =>
            new InfiniteSelectFilter(
              key,
              title,
              ({ paging, filter }) => this._searchServiceItems(paging, filter),
              true,
            ),
        },
        documentTypes: {
          icon: 'documents',
          title: 'Document type',
          persist: true,
          isVisible: () => FeatureToggles.jobTableDocumentTypeFilter(),
          factory: (key, title) =>
            new SelectFilter(
              key,
              title,
              async () => this._fetchDocumentTypeItems(),
              true,
            ),
        },
        waitingOn: {
          icon: 'pending',
          title: 'Waiting on',
          persist: true,
          isVisible: () => isRefreshDesign,
          factory: (key, title) =>
            new SelectFilter(
              key,
              title,
              FilterItemSource.static(WaitingOnOptions),
              false,
              undefined,
              parseStoredWaitingOn,
            ),
        },
        unread: {
          icon: 'notifications',
          title: 'Unread',
          persist: true,
          isVisible: () => isRefreshDesign,
          factory: (key, title) => new FlagFilter(key, title, true),
        },
      },
    })

    this._searchFilter$ = combineLatest([
      // Combine the search text changes...
      this.searchText.debouncedValue$,
      // ...with the remaining filter changes
      this.filters.appliedFilters$,
      // ...and the sort
      toStream(() => this.sorting, true),
      toStream(() => this.scope, true),
      toStream(() => this._unreadJobIds, true),
      toStream(() => this.savedFilterValues, true),
    ]).pipe(
      // Map what we've combined to a Search Filter.
      map(
        ([
          searchText,
          appliedFilters,
          sort,
          scope,
          unreadJobIds,
          savedFilterValues,
        ]): JobSearchFilter => {
          return {
            sort,
            searchText: searchText ?? '',
            scope: scope ?? 'Mine',
            ...toFilterFields(appliedFilters, unreadJobIds),
            savedFilter: savedFilterValues
              ? toFilterFields(savedFilterValues, unreadJobIds)
              : null,
          }
        },
      ),
      // Make sure to only subscribe once.
      shareLatest(),
    )

    this.searches$ = this._searchFilter$.pipe(
      // Start a non-filtered search immediately.
      startWith(JobSearchFilter.none),
      // Ignore emissions with the same search filter.
      distinctUntilChanged<JobSearchFilter>(JobSearchFilter.equals),
      // Start a search, return the filter that was used.
      switchMap((query): Observable<JobSearchResult> =>
        withAbortSignal(async (signal) => {
          let result = await this.scroller.fetch({ filter: query, signal })
          // Keep loading more until we get something back that isn't being filtered out
          // on the client. If we don't do this here, we potentially display an empty state
          // which does not trigger infinite scroll.
          while (this.jobs.length === 0 && !this.scroller.isDone) {
            signal.throwIfAborted()
            result = await this.scroller.more(signal)
          }

          return {
            searchFilter: query,
            filtersApplied: !JobSearchFilter.isNone(query),
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- A zero total falls back to the loaded job count.
            total: result?.total || this.jobs.length,
            jobs: this.jobs,
          }
        }).pipe(
          // The retry alert already reported the error; a dismissed one
          // leaves the previous results until the filter changes.
          catchError(() => EMPTY),
        ),
      ),
      // check if the user can view service item filter
      tapAction((filters) => {
        this._toggleEnableServiceItemFilter()
        return filters
      }),
      // Make sure to only subscribe once.
      shareLatest(),
    )

    // Only update the job if a filter isn't present.
    const jobUpdates$ = this._searchableJobStore.jobEvents$.pipe(
      whileActive(
        this.searches$.pipe(map((e) => JobSearchFilter.isNone(e.searchFilter))),
      ),
    )
    this._lifecycle.manage(
      merge(
        unreadJobIds$.pipe(
          distinctUntilChanged((a, b) => a.join() === b.join()),
          tapAction((ids) => (this._unreadJobIds = ids)),
        ),

        // Search requests.
        this.searches$.pipe(
          // Can't use fromStream because it will subscribe immediately,
          // which we don't want.
          tapAction((search) => {
            this._lastSearch = search
            this.totalJobs = search.total
          }),
        ),

        // When jobs are added/updated, make sure they are in the list
        jobUpdates$.pipe(
          mergeMap((e) => {
            const publicId = this._getCurrentMember().user.publicId
            const isCollaborator =
              e.job.members.some((m) => m.id === publicId) ||
              this._filterTeamMembers(publicId, e.job.clientTeamId).length > 0

            if (!e.job.archived) {
              return of(e).pipe(
                filter((e) => {
                  return (
                    !this.scroller.items.includes(e.job) &&
                    (isCollaborator || this.scope === 'All')
                  )
                }),
                tapAction((e) => {
                  this.scroller.prepend(e.job)
                  this.totalJobs++
                }),
              )
            } else {
              return of(e).pipe(
                filter(
                  (e) =>
                    this.scroller.items.includes(e.job) &&
                    (isCollaborator || this.scope === 'All'),
                ),
                tapAction((e) => {
                  this.scroller.remove(e.job)
                  this.totalJobs--
                }),
              )
            }
          }),
        ),
      ),
    )
  }

  /**
   * The internal jobs list ran through the transformer.
   */
  @computed
  get jobs() {
    const jobs = uniq(this.scroller.items)
    return this._options.models ? this._options.models(jobs) : jobs
  }

  @computed
  get isUserTypePro() {
    return this._currentUserDataType === 'Pro'
  }

  @computed
  get listPerspective(): JobListPerspective {
    if (this.isUserTypePro) {
      return 'Pro'
    }

    return this._currentUserDataType === 'Admin' ? 'Admin' : 'Client'
  }

  /**
   * The tabs shown, in the viewer's order.
   */
  @computed
  get savedFilters(): SavedJobFilter[] {
    return this.savedFilterList.visible
  }

  @computed
  get savedFilter(): SavedJobFilter | null {
    if (this.savedFilterId === sharedSavedFilterId && this.sharedFilter) {
      return {
        id: sharedSavedFilterId,
        name: this.sharedFilter.name,
        filters: this.sharedFilter.filters,
        baseId: this.sharedFilter.baseId,
        isDefault: false,
        isHidden: false,
      }
    }
    if (this.savedFilterId === null) {
      return null
    }
    // The picked ID is kept even when no tab matches, since own tabs depend
    // on a perspective that may not have resolved yet.
    return (
      this.savedFilters.find((f) => f.id === this.savedFilterId) ??
      this.defaultSavedFilter
    )
  }

  /**
   * The values the selected tab filters by, or `null` when it doesn't
   * filter. An empty tab stays `null` so "All" still counts as an
   * unfiltered search.
   */
  @computed
  get savedFilterValues(): FiltersResult<JobFilter> | null {
    const values =
      this._savedFilterPreview?.() ??
      (this.savedFilter
        ? this.filters.valuesOf(
            this.savedFilterList.filtersOf(this.savedFilter),
          )
        : null)
    return values && !isEmpty(values) ? values : null
  }

  /**
   * The saved filter selected until the viewer picks one. Only a pro is the
   * one jobs wait on; anyone else starts from every active job.
   */
  @computed
  get defaultSavedFilter(): SavedJobFilter {
    const id = this.isUserTypePro ? 'waiting-on-pro' : 'active'
    return (
      this.savedFilters.find((f) => f.id === id) ??
      this.savedFilters[0] ??
      this.savedFilterList.all[0]
    )
  }

  /**
   * Last search computed getter.
   */
  @computed
  get lastSearch() {
    return this._lastSearch
  }

  /**
   * Gets the current search state.
   */
  @computed
  get searchState(): SearchState {
    if (this._getIsFirstFetchRunning()) {
      return 'InitialSearchRunning'
    }

    if (this.lastSearch.jobs.length === 0 && this.scroller.isDone) {
      return this.lastSearch.filtersApplied
        ? 'EmptyButFilterApplied'
        : 'CompletelyEmpty'
    }

    return 'Some'
  }

  /**
   * Whether we're fetching the initial items.
   */
  @computed
  get isFetchingInitial() {
    return this.scroller.fetch.pending && !this.scroller.isRevalidating
  }

  /**
   * Determines if we should use the search jobs for an admin.
   */
  @computed
  get useSearchJobsForAdmin(): boolean {
    return this._getCurrentUserMemberships().some(
      (m) => m.member.permissions.adminJobList,
    )
  }

  /**
   * Shows the search scope filter.
   */
  @computed
  get showSearchScopeFilter() {
    return this.useSearchJobsForAdmin
  }

  /**
   * Gets the workspaces filter.
   */
  @computed
  get workspacesFilter(): SelectFilter<string, Id> {
    return this.filters.getFilter('workspaces') as SelectFilter<string, Id>
  }

  /**
   * The IDs of the workspaces the user has selected.
   */
  @computed
  get selectedWorkspaceIds() {
    return (
      this.filters.getFilter('workspaces') as SelectFilter<'workspaces', string>
    ).selected
  }

  /**
   * Gets the archived jobs filter.
   */
  @computed
  get archivedJobsFilter() {
    return this.filters.getFilter('archived') as FlagFilter<'archived'>
  }

  /**
   * Gets the job period filter.
   */
  @computed
  get jobPeriodFilter() {
    return this.filters.getFilter('jobPeriod') as SingleSelectFilter<
      string,
      JobPeriodFilterOption
    >
  }

  /**
   * Gets the job period option.
   */
  @computed
  get jobPeriodOption() {
    return this.jobPeriodFilter.value
  }

  /**
   * Collect all filtered jobs for export.
   */
  async getAllFilteredJobs(): Promise<SearchableJob[]> {
    const searchFilters = await firstValueFrom(this._searchFilter$, {
      defaultValue: undefined,
    })
    let cursor: string | null = null
    const result: SearchableJob[] = []
    if (matchesNothing(searchFilters)) {
      return result
    }
    do {
      const page: CursorPaged<SearchableJob> = await this._fetchJobs(
        toSearchJobsQuery(
          { limit: 100, after: cursor },
          searchFilters,
          this.forcedCreatedDateRange,
        ),
        this._lifecycle.signal,
      )
      result.push(...page.data)
      cursor = page.next
    } while (cursor !== null && cursor.length > 0)

    return result
  }

  /** @inheritDoc */
  activate() {
    if (!this.isUserTypePro) {
      this.filters.removeFilter('providerCutAmountRange')
    }

    if (this.useSearchJobsForAdmin) {
      this.setScope('All')
    }

    // The filters load their saved values on activation, which must happen
    // before the search subscribes, and the tracker activates managed view
    // models only after subscribing to managed observables.
    this.filters.activate()
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this.filters.deactivate()
    this._lifecycle.deactivate()
    this.setForcedCreatedDateRange(null)
  }

  /**
   * Sets the forced created date range.
   *
   * @param range
   */
  @action.bound
  setForcedCreatedDateRange(range: DateRange | null) {
    this.forcedCreatedDateRange = range
  }

  /**
   * Sets the job sort.
   *
   * @param sortMethod
   */
  @action.bound
  setSortMethod(sortMethod: JobSort) {
    this.sorting = sortMethod
  }

  /**
   * Sets the job sort.
   *
   * @param field
   */
  @action.bound
  setSortField(field: SearchableJobSortField) {
    this.setSortMethod({
      field,
      type: 'Field',
      direction: this.sorting.type === 'Field' ? this.sorting.direction : 'asc',
    })
  }

  /**
   * Sets the job scope for the search.
   *
   * @param scope
   */
  @action.bound
  setScope(scope: JobSearchScope) {
    this.scope = scope
  }

  /**
   * Searches with `getFilters` in place of the selected tab's filters while
   * they're being edited, or stops when `null`.
   */
  @action.bound
  setSavedFilterPreview(getFilters: (() => FiltersResult<JobFilter>) | null) {
    this._savedFilterPreview = getFilters
  }

  /**
   * Shows a tab someone shared and selects it.
   */
  @action.bound
  openSharedFilter(shared: SharedJobFilter) {
    this.sharedFilter = shared
    this.savedFilterId = sharedSavedFilterId
  }

  /**
   * Stops showing the shared tab.
   */
  @action.bound
  closeSharedFilter() {
    this.sharedFilter = null
  }

  /**
   * Selects a tab by ID, or the default when `id` is missing. An ID no tab
   * has reads as the default.
   */
  @action.bound
  selectSavedFilter(id: string | null | undefined) {
    this.savedFilterId = id ?? this.defaultSavedFilter.id
  }

  /**
   * Sets the job period for the search.
   */
  @action.bound
  setJobPeriod(period: JobPeriodFilterOption) {
    this.jobPeriodFilter.change(period)
  }

  /**
   * Sets the job sort.
   *
   * @param direction
   */
  @action.bound
  setSortDirection(direction: TableSortOrder) {
    if (this.sorting.type !== 'Field') {
      return
    }

    // If we have no direction remove any sorting.
    if (direction === 'none') {
      this.setSortMethod({ type: 'Default' })
      return
    }

    this.setSortMethod({
      direction,
      field: this.sorting.field,
      type: 'Field',
    })
  }

  /**
   * Clears the filters.
   */
  @action.bound
  clearFilters() {
    this.searchText.clear()
    this.filters.clearExplicitFilters()
  }

  /**
   * Adds a job to the top of the scroller.
   */
  @action.bound
  pushJob(job: SearchableJob) {
    if (this.scroller.prepend(job)) {
      this.totalJobs += 1
    }
  }

  /**
   * Removes the jobs with the given IDs from the scroller.
   */
  @action.bound
  removeJobs(ids: Id[]) {
    this.scroller.removeWhere((job) => Id.some(job.id) && ids.includes(job.id))
    this.totalJobs -= ids.length
  }

  /**
   * Loads every remaining page of jobs.
   */
  fetchAllJobs = task.resolved(async () => {
    if (this.scroller.isDone) {
      return
    }

    const signal = this._lifecycle.signal
    runInAction(() => (this.scroller.limit = 250))
    try {
      while (!this.scroller.isDone) {
        await this.scroller.more(signal)
        signal.throwIfAborted()

        // Timeout for 500ms to prevent overwhelming dotnet.
        await new Promise((resolve) => setTimeout(resolve, 500))
      }
    } finally {
      runInAction(() => (this.scroller.limit = 20))
    }
  })

  /**
   * Whether the user may list the service items of the selected workspaces.
   */
  private _canListServiceItems(): boolean {
    const member = this._getCurrentMember()
    if (member.user.platformAdmin) {
      return true
    }

    const workspaces = this.filters.appliedFilters.workspaces
    if (workspaces && workspaces.length > 0) {
      const workspaceFilter = this.workspacesFilter
      const workspaceSet = new Set(workspaceFilter.selected)

      return this._getCurrentUserMemberships()
        .filter((m) => workspaceSet.has(m.workspace.id))
        .every((m) => m.member.permissions.manageWorkspaceInventoryItems)
    }

    return member.permissions.adminManageGlobalInventoryItems
  }

  private _toggleEnableServiceItemFilter() {
    if (!this._canListServiceItems()) {
      this.filters.removeFilter('serviceItems')
    } else {
      const filter = this.filters.availableFilters.some(
        (f) => f.key === 'serviceItems',
      )
      if (
        !filter &&
        this.filters.availableFilterRegistrations.some(
          (f) => f.key === 'serviceItems',
        )
      ) {
        this.filters.addFilter('serviceItems')
      }
    }
  }

  /**
   * Fetch jobs.
   *
   * @param params
   */
  private async _fetchJobs(
    params: SearchJobsQuery,
    signal: AbortSignal,
  ): Promise<CursorPaged<SearchableJob>> {
    const page = await this._searchableJobStore.search(params, signal)

    // Retrieve latest open tickets for the loaded jobs.
    // We purposefully don't wait for this.
    void this._fetchTicketsForJobs(page.data, signal)

    return page
  }

  /**
   * Fetch legends based on filtered workspaces.
   *
   * @returns
   */
  private async _fetchLegends(): Promise<FilterItem<Id>[]> {
    const ids =
      this.workspacesFilter.selected.length > 0
        ? this.workspacesFilter.selected
        : this._getWorkspaceItems().map((w) => w.value)
    const legends: PublishedLegend[] = []
    await Promise.all(
      ids.map(
        async (w) =>
          await this._listLegends({
            workspaceId: w,
            limit: 50,
          }),
      ),
    ).then((r) => r.map((r) => legends.push(...r.data)))

    return legends.map((l) => {
      return { title: l.name, description: l.workspace.name, value: l.id }
    })
  }

  @computed
  private get _currentUserDataType() {
    const member = this._getCurrentMember()
    return this._getUserData(member.user.publicId)?.userDataType
  }

  private _getWorkspaceItems(): FilterItem<Id>[] {
    const memberships = this._getCurrentUserMemberships()
    return orderBy(
      memberships.map((m) => {
        return { title: m.workspace.name, value: m.workspace.id }
      }),
      (ws) => ws.title.toLowerCase(),
      'asc',
    )
  }

  private async _searchServiceItems(
    paging: CursorPageQuery,
    filter?: Record<string, unknown>,
  ): Promise<CursorPaged<FilterItem<Id>>> {
    const serviceItems = await this._searchInventoryItems({
      workspaceIds: this.workspacesFilter.selected,
      // A zero limit means unset.
      limit: paging.limit || 20,
      searchText: filter?.searchText as string,
      after: paging.after?.toString(),
    })

    return {
      total: serviceItems.total ?? 0,
      next: serviceItems.next,
      data: serviceItems.data.map((s) => {
        const description = s.global ? 'Global item' : s.workspace?.name
        return {
          title: s.name,
          description: !s.archived ? description : `${description} (archived)`,
          value: s.id,
        }
      }),
    }
  }

  /**
   * Fetch all teams across workspaces.
   *
   * @returns
   */
  private _fetchUserTeams(
    paging: CursorPageQuery,
    filter?: Record<string, unknown>,
  ) {
    const member = this._getCurrentMember()

    return this._listUserTeams({
      userPublicId: member.user.publicId,
      ...paging,
      ...filter,
      workspaceIds: this.workspacesFilter.value,
    }).then((teams): CursorPaged<FilterItem<Id>> => ({
      total: teams.total,
      next: teams.next,
      data: teams.data.map((t) => ({
        title: t.name,
        description: t.workspace.name,
        value: t.id,
      })),
    }))
  }

  /**
   * Retrieves open tickets for the given jobs.
   * @param jobs
   * @private
   */
  private async _fetchTicketsForJobs(
    jobs: SearchableJob[],
    signal: AbortSignal,
  ) {
    try {
      const ids = jobs.map((job) => job.id)
      await this._jobTicketStore.fetchLatestOpenTicketsForJobs(ids, signal)
    } catch (err: unknown) {
      if (isAbortError(err)) {
        return
      }

      // The tickets only decorate the list, so the list stays quiet without them.
      console.error('Unable to retrieve open tickets for jobs', err)
    }
  }

  /**
   * Retrieves the document types.
   */
  private async _fetchDocumentTypeItems(): Promise<FilterItem<Id>[]> {
    const documentTypes = await this._fetchDocumentTypes()

    return documentTypes.map((dt) => {
      return {
        title: dt.name,
        value: dt.id,
      }
    })
  }
}

/**
 * Maps an array of `JobStatusFilterOption` to an array of `JobStatus`
 *
 * @param jobStatusFilterOptions
 */
function toJobStatuses(
  jobStatusFilterOptions?: JobStatusFilterOption[] | null,
): JobStatus[] | undefined {
  return jobStatusFilterOptions?.flatMap((jobStatusFilterOption) => {
    switch (jobStatusFilterOption) {
      case 'Current':
        return ['Claimed', 'Unclaimed', 'UnderConstruction']
      case 'OnHold':
        return ['OnHold']
      case 'Idle':
        return ['Idle']
      case 'Closed':
        return ['Closed']
      case 'InProgress':
        return ['Claimed']
      case 'InfoGathering':
        return ['InfoGathering']
      case 'Pending':
        return ['Pending']
      case 'Unclaimed':
        return ['Unclaimed']
      case 'Onboarding':
        return ['UnderConstruction']
    }
  })
}

/**
 * Maps an array of `JobProgressionStepFilterOption` to an array of `JobProgressionStepStatus`
 *
 * @param progressionStepFilterOptions
 */
function toJobProgressionSteps(
  progressionStepFilterOptions?: JobProgressionStepFilterOption[] | null,
): JobProgressionStepStatus[] | undefined {
  return progressionStepFilterOptions?.map((filterOption) => {
    switch (filterOption) {
      case 'OpenItems':
        return 'OpenItems'
      case 'JobStarted':
        return 'JobStarted'
      case 'DraftInReview':
        return 'DraftInReview'
      case 'DraftApproved':
        return 'DraftApproved'
      case 'DraftRejected':
        return 'DraftRejected'
      case 'AuthorizationRequested':
        return 'AuthorizationRequested'
      case 'AuthorizationSigned':
        return 'AuthorizationSigned'
      case 'ReturnFiled':
        return 'ReturnFiled'
      case 'None':
        return 'Unspecified'
      case 'NotStarted':
        return 'NotStarted'
      case 'PreparationCompleted':
        return 'PreparationCompleted'
      case 'ReviewCompleted':
        return 'ReviewCompleted'
      case 'ExtensionInProgress':
        return 'ExtensionInProgress'
      case 'ExtensionConfirmed':
        return 'ExtensionConfirmed'
      case 'ExtensionAccepted':
        return 'ExtensionAccepted'
      case 'ExtensionRejected':
        return 'ExtensionRejected'
      case 'WaitingOnClient':
        return 'ProgressionWaitingOnClient'
      case 'ReadyToFile':
        return 'ReadyToFile'
    }
  })
}

/**
 * Maps an array of `JobScopeChangeStatusFilterOption` to an array of `JobScopeChangeStatus`
 *
 * @param jobScopeChangeStatusFilterOption
 */
function toJobScopeChangeStatus(
  jobScopeChangeStatusFilterOption?: JobScopeChangeStatusFilterOption[] | null,
): JobScopeChangeStatus[] | undefined {
  if (!jobScopeChangeStatusFilterOption) {
    return undefined
  }

  const result: JobScopeChangeStatus[] = []

  jobScopeChangeStatusFilterOption.forEach((filterOption) => {
    switch (filterOption) {
      case 'Accepted':
        result.push('Accepted')
        break
      case 'Rejected':
        result.push('Rejected')
        result.push('ApplyFailed')
        break
      case 'PendingReview':
        result.push('PendingFinalization')
        result.push('PendingAdminApproval')
        result.push('PendingClientApproval')
        break
    }
  })

  return result
}

/**
 * Whether the filter can't match any job, so there is no point asking. An
 * empty id list would otherwise drop the constraint and match every job.
 */
function matchesNothing(filter: JobSearchFilter | undefined): boolean {
  return (
    filter?.unreadJobIds?.length === 0 ||
    filter?.savedFilter?.unreadJobIds?.length === 0
  )
}

/**
 * Maps applied filter values to the search filter fields they set.
 */
function toFilterFields(
  filters: FiltersResult<JobFilter>,
  unreadJobIds: Id[],
): JobFilterFields {
  return {
    workspaceIds: filters.workspaces,
    jobType: filters.jobType,
    status: filters.jobStatus ?? null,
    progressionStep: filters.progressionStep ?? null,
    jobScopeChangeStatus: filters.jobScopeUpdates ?? null,
    createdDateRange: filters.createdRange ?? null,
    transmittedDateRange: filters.submittedRange ?? null,
    acceptedDateRange: filters.claimedRange ?? null,
    closedDateRange: filters.closedRange ?? null,
    providerCutAmountRange: filters.providerCutAmountRange ?? null,
    period: filters.jobPeriod ?? 'All',
    team: filters.team,
    archived: filters.archived ?? false,
    readyToAdvance: filters.readyToAdvance ?? false,
    memberIds: filters.members,
    serviceItems: filters.serviceItems ?? [],
    documentTypeShortIds: filters.documentTypes ?? [],
    waitingOn: filters.waitingOn?.[0] ?? null,
    unreadJobIds: filters.unread ? unreadJobIds : null,
  }
}

/**
 * Creates a `SearchJobsQuery` from the paging and filter.
 *
 * @param paging
 * @param filter
 */
function toSearchJobsQuery(
  paging: CursorPageQuery,
  filter: JobSearchFilter | undefined,
  forcedCreatedDateRange: DateRange | null,
): SearchJobsQuery {
  const filterQueries = [
    toFilterQuery(filter, forcedCreatedDateRange),
    filter?.savedFilter && toFilterQuery(filter.savedFilter, null),
  ].filter((q): q is string => !!q)

  const query = {
    searchText: filter?.searchText,
    filter:
      filterQueries.length > 1
        ? filterQueries.map((q) => `(${q})`).join(' AND ')
        : (filterQueries[0] ?? ''),
    limit: paging.limit,
    after: paging.after,
    scope: filter?.scope ?? 'Mine',
  } as SearchJobsQuery

  if (filter?.sort.type === 'Field') {
    query.orderBy = `${filter.sort.field} ${
      filter.sort.direction === 'desc' ? 'desc' : 'asc'
    }`
  } else if (!query.searchText) {
    // Only apply the default sort when search text isn't provided
    query.orderBy = 'date_last_activity desc'
  }

  return query
}

/**
 * Builds the query string for one set of filter fields.
 */
function toFilterQuery(
  filter: JobFilterFields | undefined,
  forcedCreatedDateRange: DateRange | null,
): string {
  return new JobSearchQueryStringBuilder()
    .withWorkspaceIds(filter?.workspaceIds)
    .withLegendIds(filter?.jobType)
    .withStatuses(toJobStatuses(filter?.status))
    .withProgressionSteps(toJobProgressionSteps(filter?.progressionStep))
    .withJobScopeChangeStatus(
      toJobScopeChangeStatus(filter?.jobScopeChangeStatus),
    )
    .withinDateCreatedRange(forcedCreatedDateRange ?? filter?.createdDateRange)
    .withinDateClosedRange(filter?.closedDateRange)
    .withinDateClaimedRange(filter?.acceptedDateRange)
    .withinDateSubmittedRange(filter?.transmittedDateRange)
    .withProviderCutAmountRange(filter?.providerCutAmountRange)
    .withTeamIds(filter?.team)
    .withPeriod(filter?.period ?? 'All')
    .withArchivalStatus(filter?.archived)
    .withReadyToAdvance(filter?.readyToAdvance)
    .withMemberIds(filter?.memberIds)
    .withServiceItems(filter?.serviceItems)
    .withDocumentTypeShortIds(filter?.documentTypeShortIds)
    .withWaitingOn(filter?.waitingOn)
    .withJobIds(filter?.unreadJobIds)
    .build()
}

/**
 * Job sorting type.
 */
export type JobSort = DefaultSort | FieldSort

/**
 * Default sort.
 */
export interface DefaultSort {
  type: 'Default'
}

/**
 * Field sort.
 */
export interface FieldSort {
  type: 'Field'
  field: SearchableJobSortField
  direction: TableSortOrder
}

/**
 * Job search filter.
 */
export interface JobSearchFilter {
  searchText?: string
  workspaceIds?: Id[] | null
  jobType?: Id[] | null
  createdDateRange?: DateRange | null
  transmittedDateRange: DateRange | null
  acceptedDateRange: DateRange | null
  closedDateRange: DateRange | null
  status: JobStatusFilterOption[] | null
  progressionStep: JobProgressionStepFilterOption[] | null
  jobScopeChangeStatus: JobScopeChangeStatusFilterOption[] | null
  providerCutAmountRange?: NumberRange | null
  sort: JobSort
  scope: JobSearchScope
  period: JobPeriodFilterOption
  team?: Id[] | null
  archived: boolean
  readyToAdvance: boolean
  memberIds?: Id[] | null
  serviceItems: Id[] | null
  documentTypeShortIds: Id[] | null
  waitingOn?: WaitingOn | null
  unreadJobIds?: Id[] | null
  /**
   * The selected saved filter's fields. The search matches both these and
   * the fields above.
   */
  savedFilter?: JobFilterFields | null
}

/**
 * The search filter fields that come from filter values.
 */
export type JobFilterFields = Omit<
  JobSearchFilter,
  'searchText' | 'sort' | 'scope' | 'savedFilter'
>

export const JobSearchFilter = {
  /**
   * An empty search filter.
   */
  none: {
    searchText: '',
    workspaceIds: [],
    jobType: undefined,
    createdDateRange: null,
    transmittedDateRange: null,
    acceptedDateRange: null,
    closedDateRange: null,
    status: null,
    progressionStep: null,
    jobScopeChangeStatus: null,
    providerCutAmountRange: null,
    sort: { type: 'Default' },
    scope: 'Mine',
    period: 'All',
    team: undefined,
    archived: false,
    readyToAdvance: false,
    memberIds: [],
    serviceItems: [],
    documentTypeShortIds: [],
    waitingOn: null,
    unreadJobIds: null,
    savedFilter: null,
  } as JobSearchFilter,

  /**
   * Whether the specified search filter is considered empty.
   */
  isNone: (searchFilter: JobSearchFilter) =>
    JobSearchFilter.equals(
      omit(JobSearchFilter.none, 'sort', 'scope'),
      omit(searchFilter, 'sort', 'scope'),
    ),

  /**
   * Whether 2 search filters are considered equal.
   */
  equals: (left: Partial<JobSearchFilter>, right: Partial<JobSearchFilter>) =>
    deepEqual(left, right),
}

/**
 * Search result.
 */
export interface JobSearchResult {
  /**
   * The search filter that was used.
   */
  searchFilter: JobSearchFilter
  /**
   * Whether any search filters were applied.
   */
  filtersApplied: boolean
  /**
   * The total number of search results (not just what was returned).
   */
  total: number
  /**
   * The current list of jobs contained in the searcher.
   */
  readonly jobs: readonly SearchableJob[]
}

/**
 * Current state of searching: the initial search is running, it returned
 * something, it returned nothing although a filter was applied, or it returned
 * nothing with no filters applied.
 */
export type SearchState =
  'InitialSearchRunning' | 'Some' | 'EmptyButFilterApplied' | 'CompletelyEmpty'

/**
 * Factory for creating a job search view model.
 */
export type JobSearchViewModelFactory = ReturnType<
  typeof createJobSearchViewModelFactory
>

/** @see JobSearchViewModelFactory */
export function createJobSearchViewModelFactory(
  searchableJobStore: SearchableJobStore,
  jobTicketStore: JobTicketStore,
  createAlertRetryWrapper: AlertRetryWrapperFactory,
  getCurrentMember: GetCurrentMemberFn,
  getUserData: GetUserDataFn,
  getCurrentUserMemberships: GetCurrentUserWorkspaceMembershipsFn,
  filterTeamMembers: FilterTeamMembersFn,
  listLegends: ListLegendsFn,
  listUserTeams: ListUserTeamsFn,
  searchInventoryItems: ListInventoryItemsFn,
  fetchDocumentTypes: FetchDocumentTypesFn,
  unreadJobIds$: Observable<Id[]>,
) {
  return (options?: JobSearchViewModelOptions) =>
    new JobSearchViewModel(
      searchableJobStore,
      jobTicketStore,
      createAlertRetryWrapper,
      getCurrentMember,
      getUserData,
      getCurrentUserMemberships,
      filterTeamMembers,
      listLegends,
      listUserTeams,
      searchInventoryItems,
      fetchDocumentTypes,
      unreadJobIds$,
      options,
    )
}
